import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import DialogContent from '@material-ui/core/DialogContent'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import FileDownloadOutlinedIcon from 'components/material-symbol/icons/FileDownload'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import GroupEventAudienceMessageDialog from 'components/stream-messaging/group-event-audience-message-dialog'
import MessageOutlined from 'components/material-symbol/icons/Message'
import { Link as RouterLink, useRouteMatch } from 'react-router-dom'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import EnhancedTable from 'components/tables/enhanced-table'
import Waitlist from './waitlist'
import PendingInvitesList from './pending-invites'
import AttendeeListItem from './attendee-list-item'
import { generateParticipantCsvData, getEventRegisterPath } from './utils'
import AttendanceModal from '../attendance-modal'
import SubviewPageHeader from '../subview-page-header'
import CopyTextButton from 'components/copy-text-button'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import PeopleAltOutlinedIcon from 'components/material-symbol/icons/PeopleAlt'
import LinkOutlinedIcon from 'components/material-symbol/icons/Link'
import { gtmEvent } from 'lib/gtm'
import { useStyles } from './styles'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { PaymentDetails } from './payment-details'

export { PaymentDetails }

const ExportParticipantsButton = ({
  event,
  exportOptions,
  isHopeEvent,
  classes,
  onExport,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const hasMultipleOptions = exportOptions.length > 1

  const getCsvProps = option => ({
    data: generateParticipantCsvData(
      event.enrollments,
      event?.waitlist?.entries,
      isHopeEvent,
      option.value === 'waitlist',
      option.value === 'canceled attendees',
    ),
    filename: `${event.title}-${event.startDateTime}-${option.value}.csv`,
    onClick: onExport,
  })

  if (!hasMultipleOptions) {
    const option = exportOptions[0]
    return (
      <Button
        variant="outlined"
        color="primary"
        component={CSVLink}
        startIcon={<FileDownloadOutlinedIcon />}
        className={classes.exportButton}
        {...getCsvProps(option)}
      >
        Export
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<FileDownloadOutlinedIcon />}
        className={classes.exportButton}
        onClick={menuEvent => setAnchorEl(menuEvent.currentTarget)}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {exportOptions.map(option => (
          <MenuItem
            key={option.value}
            component={CSVLink}
            className={classes.exportMenuItem}
            onClick={() => {
              onExport()
              setAnchorEl(null)
            }}
            {...getCsvProps(option)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const BookStudentButton = ({
  event,
  className,
  children = 'Book a student',
}) => {
  const registerPath = getEventRegisterPath(event)

  if (!registerPath) {
    return null
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      component={RouterLink}
      to={registerPath}
      className={className}
      onClick={() =>
        gtmEvent({
          event: 'formSubmit',
          formCategory: 'book-group-event',
          formAction: 'clicked-book-student-button',
        })
      }
    >
      {children}
    </Button>
  )
}

const AttendeesEmptyState = ({ event }) => {
  const classes = useStyles()
  const shareUrl = event.externalRegistrationUrl || event.eventUrl

  return (
    <Box className={classes.emptyState}>
      <CircularHighlightedIcon
        icon={PeopleAltOutlinedIcon}
        size={64}
        iconSize={28}
        highlightColor="#C5A561"
        iconColor="#FFFFFF"
      />
      <Typography variant="h6" className={classes.emptyStateTitle}>
        No attendees yet
      </Typography>
      <Typography variant="body2" className={classes.emptyStateCaption}>
        Share your registration link so students can sign up for this event.
      </Typography>
      <Box className={classes.emptyStateActions}>
        <CopyTextButton
          value={shareUrl}
          component={Button}
          variant="contained"
          className={classes.tonalButton}
          notificationText="Link copied to clipboard"
          startIcon={<LinkOutlinedIcon />}
          disabled={!shareUrl}
        >
          Share registration link
        </CopyTextButton>
        <BookStudentButton event={event} />
      </Box>
    </Box>
  )
}

const attendeeColumns = [
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    width: '50%',
    mobile: true,
    sortable: false,
  },
  {
    id: 'status',
    label: 'Status',
    align: 'left',
    width: '30%',
    mobile: true,
    sortable: false,
  },
  {
    id: 'actions',
    label: '',
    align: 'right',
    width: '20%',
    mobile: true,
    sortable: false,
  },
]

const ParticipantsList = ({
  event,
  enrollments,
  isHopeEvent = false,
  label = 'Attendees',
}) => {
  const { path } = useRouteMatch()
  const { user } = useAuth()
  const isUserPrimaryCoach = user?.id === event.coach.id
  const studentProfileBasePath = `${path
    .split('/')
    .slice(0, 2)
    .join('/')}/${event.slug}`

  const rows = enrollments.map(enrollment => (
    <AttendeeListItem
      key={enrollment.id}
      enrollment={enrollment}
      event={event}
      isHopeEvent={isHopeEvent}
      studentProfileBasePath={studentProfileBasePath}
      isUserPrimaryCoach={isUserPrimaryCoach}
    />
  ))

  return (
    <EnhancedTable
      rows={rows}
      columns={attendeeColumns}
      label={label}
      variant="list"
      showHeaderPagination={false}
      showPaginator={false}
      emptyState={() => null}
    />
  )
}

const EventParticipantsContent = ({ setIsOpen, event, embedded = false }) => {
  const { tags } = event
  const classes = useStyles({ embedded })
  const [invitesEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_REGISTRATION_INVITES,
  ])

  const enrollmentSource = event.enrollments ?? []
  const canceledEnrollments = enrollmentSource.filter(e => e.isCanceled)
  const enrollments = enrollmentSource.filter(e => !e.isCanceled)
  const isHopeEvent = tags.includes('PGA HOPE')
  const waitlistCount = event?.waitlist?.entries.length
  const showWaitlist = waitlistCount > 0
  const hasRegisteredAttendees = enrollments.length > 0
  const registrationInvites = event.registrationInvites ?? []
  const pendingInvites = registrationInvites.filter(
    invite => invite.status === 'pending',
  )

  const exportOptions = [{ value: 'attendees', label: 'Export Attendees' }]

  canceledEnrollments.length > 0 &&
    exportOptions.push({
      value: 'canceled attendees',
      label: 'Export Canceled Attendees',
    })

  showWaitlist &&
    exportOptions.push({
      value: 'waitlist',
      label: 'Export Waitlist',
    })

  return (
    <>
      <SubviewPageHeader
        eventTitle={event.title}
        pageTitle="Attendees"
        onBack={() => setIsOpen('detail')}
        actions={
          <>
            {hasRegisteredAttendees ? (
              <>
                <SensitiveInfoWrapper>
                  <GroupEventAudienceMessageDialog
                    event={event}
                    trigger={
                      <IconButton color="primary" aria-label="Message">
                        <MessageOutlined />
                      </IconButton>
                    }
                  />
                </SensitiveInfoWrapper>
                <ExportParticipantsButton
                  event={event}
                  exportOptions={exportOptions}
                  isHopeEvent={isHopeEvent}
                  classes={classes}
                  onExport={() => setIsOpen('detail')}
                />
                <BookStudentButton
                  event={event}
                  className={classes.headerActionButton}
                />
              </>
            ) : null}
            {event?.collectAttendance &&
            event?.currentSession &&
            hasRegisteredAttendees ? (
              <AttendanceModal
                trigger={
                  <Button variant="contained" color="primary">
                    Attendance
                  </Button>
                }
                event={event}
              />
            ) : null}
          </>
        }
      />
      {hasRegisteredAttendees ? (
        <ParticipantsList
          event={event}
          enrollments={enrollments}
          isHopeEvent={isHopeEvent}
        />
      ) : (
        <AttendeesEmptyState event={event} />
      )}
      {canceledEnrollments.length > 0 && (
        <>
          <hr />
          <Typography variant="h6" className={classes.headerFont}>
            Canceled Attendees ({canceledEnrollments.length})
          </Typography>
          <ParticipantsList
            event={event}
            enrollments={canceledEnrollments}
            isHopeEvent={isHopeEvent}
          />
        </>
      )}
      {invitesEnabled && pendingInvites.length > 0 && (
        <>
          <hr />
          <Typography variant="h6" className={classes.headerFont}>
            Pending invites ({pendingInvites.length})
          </Typography>
          <PendingInvitesList
            invites={pendingInvites}
            groupEventId={event.id}
          />
        </>
      )}
      {showWaitlist && (
        <>
          <hr />
          <Typography variant="h6" className={classes.headerFont}>
            Waitlist ({waitlistCount})
          </Typography>
          <Waitlist
            contacts={event.waitlist.entries}
            groupEventId={event.id}
            registrationInvites={registrationInvites}
            invitesEnabled={invitesEnabled}
          />
        </>
      )}
    </>
  )
}

const EventParticipants = ({ setIsOpen, event, embedded = false }) => {
  const classes = useStyles({ embedded })

  if (embedded) {
    return (
      <Box className={classes.embeddedRoot}>
        <EventParticipantsContent {...{ setIsOpen, event, embedded }} />
      </Box>
    )
  }

  return (
    <DialogContent>
      <EventParticipantsContent {...{ setIsOpen, event, embedded }} />
    </DialogContent>
  )
}

export default EventParticipants
